import { getSearchHandle } from '@lib/data/search-handle';
import { useEffect, useRef, useState } from 'react';
import { BusinessInfo } from 'types/business';
import Slider from 'react-slick';
import ReadonlyRating from '@modules/common/components/readonly-rating';
import { YelpLogo } from '@modules/common/components/yelp-logo/yelp-logo';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/solid';
import { GoogleLogo } from '@modules/common/components/google-logo';
import { ImageProduct } from './ImageProduct';
import { useRouter } from 'next/dist/client/components/navigation';
import { useAvailableCyoThemeCards } from '@lib/hooks/use-availableCyoThemeCards';
import { DEFAULT_SEARCH_PROVIDER } from '@lib/constants';
import { useSearchService } from '@lib/context/search-service-context';
import { GoogleRating } from '@modules/common/components/google-rating';


function NextArrow(props: any) {
  const { className, style, onClick } = props;
  return (
    <div className={className} style={{ ...style, display: 'block' }} onClick={onClick}>
      <ChevronRightIcon />
    </div>
  );
}

function PrevArrow(props: any) {
  const { className, style, onClick } = props;
  return (
    <div className={className} style={{ ...style, display: 'block' }} onClick={onClick}>
      <ChevronLeftIcon />
    </div>
  );
}

export const GeolocationTopResults = ({ business }: { business: BusinessInfo }) => {
  const { giftCardTypesText } = useAvailableCyoThemeCards();
  const { currentSearch } = useSearchService();
  const router = useRouter();
  const [data, setData] = useState<any>([]);
  const [isDragging, setIsDragging] = useState(false);

  const startPos = useRef({ x: 0, y: 0 });
  const settings = {
      speed: 500,
      rows: 3, // Always 3 rows
      slidesPerRow: 4, // 4 columns on desktop
      slidesToShow: 1, // Show 1 complete grid (3x4 = 12 cards)
      slidesToScroll: 1, // But scroll by 1 column worth of cards
      infinite: true,
      swipe: true,
      touchMove: true,
      draggable: true,
      arrows: true,
      nextArrow: <NextArrow />,
      prevArrow: <PrevArrow />,
      dots: false,
      centerMode: false,
      variableWidth: false,
      responsive: [
          {
              breakpoint: 1875,
              settings: {
                rows: 3, // Always 3 rows
                slidesPerRow: 3, // 3 columns
                slidesToShow: 1,
                slidesToScroll: 1
              },
          },
          {
              breakpoint: 1210,
              settings: {
                rows: 3, // Always 3 rows
                slidesPerRow: 2, // 2 columns
                slidesToShow: 1,
                slidesToScroll: 1
              },
          },
          {
              breakpoint: 768,
              settings: {
                rows: 3, // Keep 3 rows even on mobile
                slidesPerRow: 1, // 1 column on mobile
                slidesToShow: 1,
                slidesToScroll: 1,
                arrows: true // Keep arrows on mobile
              },
          },
          {
              breakpoint: 480,
              settings: {
                rows: 3, // Keep 3 rows
                slidesPerRow: 1, // 1 column
                slidesToShow: 1,
                slidesToScroll: 1,
                arrows: true // Keep arrows on mobile
              },
          },
      ],
  };

  const getData = async () => {
    try {
      let location = business?.location?.display_address?.join(', ');
      if (business?.location?.city) {
        location = `${business?.location?.city}, ${business?.location?.state} ${business?.location?.country}`;
      }
      // Use card_program if available, otherwise fall back to parent_category
      const category = business?.card_program || business?.parent_category || '';
      const response = await getSearchHandle(
        location,
        category,
        category,
        24,
        0,
        'best_match',
        true,
        business?.search_provider || currentSearch || DEFAULT_SEARCH_PROVIDER,
        '',
        null,
        true
      );
      setData([...response.businesses]);
    } catch (error) {}
  };

  useEffect(() => {
    // Show top results if we have either card_program or parent_category
    if (business?.card_program || business?.parent_category) {
      getData();
    }
  }, [business]);

  const extractCityStateZip = (address: string): string | null => {
    const match = address.split(',');
    if (match) {
      if (match.length >= 2) {
        return match.slice(-2).join(',').trim();
      }
    }
    return null;
  };

  const handleMouseDown = (e: any) => {
    startPos.current = { x: e.clientX, y: e.clientY };
    setIsDragging(false);
  };

  const handleMouseMove = (e: any) => {
    const dx = Math.abs(e.clientX - startPos.current.x);
    const dy = Math.abs(e.clientY - startPos.current.y);
    if (dx > 5 || dy > 5) setIsDragging(true);
  };

  return (
    data?.length > 0 && (
      <div className="giftCardSimilarTopResult slider-container">
        <h2 className="giftCardSimilarGifts-title">
          <span className="font-normal">
            Top <span className="capitalize">{giftCardTypesText?.[business?.card_program || business?.parent_category] || (business?.parent_category || 'Places')}</span> in
          </span>{' '}
          <br />
          {extractCityStateZip(business?.location?.display_address?.join(', '))}
        </h2>
        <div onMouseDown={handleMouseDown} onMouseMove={handleMouseMove}>
          <Slider {...settings}>
            {data?.map((item: any) => (
              <div key={item?.id} className="px-2">
                <div
                  className="giftCardSimilarTopResult-item pointer"
                  onClick={() => {
                    if (isDragging) return;
                    // Ensure searchBy param is included for Google searches
                    const searchProvider = item.search_provider || business.search_provider || currentSearch || DEFAULT_SEARCH_PROVIDER;
                    const url = item.alias.includes('?')
                        ? `${item.alias}&searchBy=${searchProvider}`
                        : `${item.alias}?searchBy=${searchProvider}`;
                    router.push(url);
                  }}
                >
                  <div>
                    <div className="flex flex-col justify-center min-h-[96px]">
                      <div className="grid">
                        <h3 dangerouslySetInnerHTML={{ __html: item?.name }} className="line-clamp-2" />
                      </div>
                      {!item?.hasCYO && (
                        <div className="giftCardSimilarTopResult-rating">
                        {business?.search_provider == 'google' ? <GoogleRating count={item?.rating} size="1rem" /> :<ReadonlyRating count={item?.rating} size="12px" />}
                          <div className="flex items-center ml-1 gap-1 mt-1">
                            <span>{item?.review_count}</span>
                            <strong>on</strong>
                            <span className="scale-90 -mt-1">
                            {business?.search_provider == 'google' ? <GoogleLogo /> : <YelpLogo />}
                          </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <ImageProduct business={item} className="w-[103px] h-[96px] flex-none rounded-lg bg-center bg-cover" />
                </div>
              </div>
            ))}
          </Slider>
        </div>
      </div>
    )
  );
};
